import { useEffect, useMemo, useState } from "react";
import { Select, Typography } from "antd";
import { CarOutlined, PlusOutlined } from "@ant-design/icons";
import { fetchWorkflowVehicleNumbers } from "../../services/workflowUpload";
import { buildVehicleOptions } from "../../utils/vehicleOptions";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { toastError } from "../../utils/toast";

const { Text } = Typography;

interface VehicleSelectProps {
  value: string | null;
  onChange: (vehicleNumber: string | null) => void;
}

const VehicleSelect = ({ value, onChange }: VehicleSelectProps) => {
  const [vehicles, setVehicles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchWorkflowVehicleNumbers()
      .then((list) => {
        if (active) setVehicles(list);
      })
      .catch((error) => {
        if (active) toastError(getErrorMessage(error, "Failed to load vehicles"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const options = useMemo(
    () =>
      buildVehicleOptions(vehicles, search).map((option) => ({
        value: option.value,
        label: option.isNew ? (
          <Text>
            <PlusOutlined style={{ marginRight: 6 }} />
            Add &ldquo;{option.value}&rdquo;
          </Text>
        ) : (
          option.value
        ),
      })),
    [vehicles, search]
  );

  return (
    <Select
      showSearch
      allowClear
      loading={loading}
      value={value ?? undefined}
      searchValue={search}
      onSearch={setSearch}
      placeholder="Select or type a vehicle number"
      suffixIcon={<CarOutlined />}
      style={{ width: "100%", maxWidth: 360 }}
      options={options}
      filterOption={false}
      onChange={(next) => {
        onChange(next ?? null);
        setSearch("");
      }}
      notFoundContent={loading ? "Loading…" : "Type to add a vehicle number"}
    />
  );
};

export default VehicleSelect;
